import Dialog from '@mui/material/Dialog'
import { Tab, Tabs } from '@mui/material'
import React, { useEffect, useState } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { Loader } from 'src/designSystem'
import { CallBookingParticipantFragment } from '../videoCallBookingHeaderQueries.generated'
import {
  CoachAvailabilityVideoCallSlot,
  useCoachAvailabilityDays,
  CoachAvailabilityDay,
} from './useCoachAvailabilityDays'
import BookingDateSelection from './BookingDateSelection'
import { NoTherapistsAvailable } from './NoTherapistAvailable'
import CoachProfileList from './CoachProfileList/CoachProfileList'
import ManualSchedulingView from './ManualScheduling/ManualSchedulingView'

type Props = {
  onCloseDialog: () => void
  participant: CallBookingParticipantFragment
}

const IntakeBookingContainerDialog = ({ onCloseDialog, participant }: Props) => {
  const styles = useStyles()
  const [activeTab, setActiveTab] = useState(0)
  const [selectedDayId, setSelectedDayId] = useState<string | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<CoachAvailabilityVideoCallSlot | null>(null)
  const { coachAvailabilityDays, loading } = useCoachAvailabilityDays({ participant })

  const selectedAvailabilityDay: CoachAvailabilityDay | undefined = coachAvailabilityDays?.find(
    (availabilityDay) => availabilityDay.dayId === selectedDayId
  )

  useEffect(() => {
    if (!selectedDayId && coachAvailabilityDays?.[0]) {
      setSelectedDayId(coachAvailabilityDays[0].dayId)
    }
  }, [coachAvailabilityDays, selectedDayId])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    setSelectedDayId(coachAvailabilityDays?.[0]?.dayId ?? null)
    setSelectedSlot(null)
  }

  return (
    <Dialog onClose={onCloseDialog} open>
      <div className={styles.container}>
        <Tabs value={activeTab} onChange={handleTabChange} className={styles.tabs}>
          <Tab label="Available slots" />
          <Tab label="Schedule manually" />
        </Tabs>

        {activeTab === 0 && (
          <>
            {loading ? (
              <Loader size="large" />
            ) : (
              <>
                {coachAvailabilityDays?.length === 0 || !selectedAvailabilityDay ? (
                  <NoTherapistsAvailable onCloseDialog={onCloseDialog} />
                ) : (
                  <>
                    <div className={styles.stickyHeader}>
                      <BookingDateSelection
                        days={coachAvailabilityDays}
                        selectedDayId={selectedDayId}
                        onDaySelection={(dayId: string) => {
                          setSelectedDayId(dayId)
                          setSelectedSlot(null)
                        }}
                      />
                    </div>
                    <div className={styles.scrollableContent}>
                      <CoachProfileList
                        selectedDay={selectedAvailabilityDay}
                        selectedSlot={selectedSlot}
                        onCallSlotPress={(slot: CoachAvailabilityVideoCallSlot | null) => setSelectedSlot(slot)}
                        onCloseDialog={onCloseDialog}
                        participant={participant}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        {activeTab === 1 && <ManualSchedulingView participant={participant} onCloseDialog={onCloseDialog} />}
      </div>
    </Dialog>
  )
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem',
    minWidth: '500px',
    maxHeight: '80vh',
  },
  tabs: {
    marginBottom: theme.spacing(1),
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    paddingBottom: theme.spacing(2),
  },
  scrollableContent: {
    overflowY: 'auto',
  },
}))

export default IntakeBookingContainerDialog
